package com.exura.pages.planCreator.model

import androidx.compose.runtime.Composable
import com.exura.components.Icon
import com.exura.features.plandefinition.domain.model.series.*
import com.exura.ui.*
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.borderRadius
import com.varabyte.kobweb.compose.ui.modifiers.padding
import org.jetbrains.compose.web.css.CSSLengthOrPercentageValue
import org.jetbrains.compose.web.css.px


enum class SpawnedElementsInfo(val spawnType: SeriesSpawnType, val accentColor: ExuraRgba) {
    INSTRUCTION(SeriesSpawnType.INSTRUCTION, ExuraColors.INSTRUCTION),
    QUESTION(SeriesSpawnType.QUESTION, ExuraColors.QUESTION),
    PERSISTENT(SeriesSpawnType.PERSISTENT, ExuraColors.PERSISTENT),
    FLOATING(SeriesSpawnType.FLOATING, ExuraColors.DYNAMIC),
    UNKNOWN(SeriesSpawnType.INSTRUCTION, ExuraColors.BLUE_DARK)
}

// TODO: This should just be changed to enum, with correct mapping to shared enum
data class SeriesTypeInfo(
    val seriesType: SeriesType,
    val seriesSpawnInfo: SpawnedElementsInfo,
    val name: String,
    val prompt: String,
    val colorOverride: ExuraRgba? = null,
    val accentColor: ExuraRgba = colorOverride ?: seriesSpawnInfo.accentColor,
    val spawnedIcon:  (@Composable () -> Unit),
    val icon: @Composable () -> Unit,
)

object SeriesTypeRegistry {

    private val typeInfoMap: Map<SeriesType, SeriesTypeInfo> = mapOf(
        SeriesType.SIMPLE_INSTRUCTION to SeriesTypeInfo(
            seriesType = SeriesType.SIMPLE_INSTRUCTION,
            seriesSpawnInfo = SpawnedElementsInfo.INSTRUCTION,
            name = "Prosta Seria Zadań",
            prompt = "Zleć pacjentowi powtarzalne instrukcje, jak ćwiczenia, czy dawkowanie leków",
            spawnedIcon = { InstructionIcon() },
        ) {
            InstructionIcon()
        },

        SeriesType.SIMPLE_QUESTION to SeriesTypeInfo(
            seriesType = SeriesType.SIMPLE_QUESTION,
            seriesSpawnInfo = SpawnedElementsInfo.QUESTION,
            name = "Seria Pytań",
            prompt = "Zbieraj informacje od pacjenta poprzez pytania kontrolne",
            spawnedIcon = { QuestionIcon() },
        ) {
            QuestionIcon()
        },

        SeriesType.SIMPLE_PERSISTENT to SeriesTypeInfo(
            seriesType = SeriesType.SIMPLE_PERSISTENT,
            seriesSpawnInfo = SpawnedElementsInfo.PERSISTENT,
            name = "Seria Ograniczeń",
            prompt = "Ustaw trwałe ograniczenia i rekomendacje dla pacjenta",
            spawnedIcon = { PersistentIcon() },
        ) {
            PersistentIcon()
        },

        SeriesType.SIMPLE_FLOATING to SeriesTypeInfo(
            seriesType = SeriesType.SIMPLE_FLOATING,
            seriesSpawnInfo = SpawnedElementsInfo.FLOATING,
            name = "Seria Dynamiczna",
            prompt = "Dodaj elastyczne elementy, które dostosowują się do postępów pacjenta",
            spawnedIcon = { FloatingIcon() },
        ) {
            FloatingIcon()
        },

        SeriesType.UNKNOWN to SeriesTypeInfo(
            seriesType = SeriesType.UNKNOWN,
            seriesSpawnInfo = SpawnedElementsInfo.INSTRUCTION,
            name = "Nieznana opcja",
            prompt = "",
            spawnedIcon = { InstructionIcon() },
        ) {
            InstructionIcon()
        }
    )

    fun getTypeInfo(seriesType: SeriesType?): SeriesTypeInfo? {
        return typeInfoMap[seriesType]
    }

    fun getTypeInfo(series: BaseSeries<*>?): SeriesTypeInfo? {
        return getTypeInfo(seriesToType(series))
    }

    fun getAllAvailableTypes(): List<SeriesTypeInfo> {
        return typeInfoMap.values.filter { it.seriesType != SeriesType.UNKNOWN }.toList()
    }

    fun seriesToType(series: BaseSeries<*>?): SeriesType? {
        return when (series) {
            is SimpleInstructionSeries -> SeriesType.SIMPLE_INSTRUCTION
            is SimplePersistentSeries -> SeriesType.SIMPLE_PERSISTENT
            is SimpleFloatingSeries -> SeriesType.SIMPLE_FLOATING
            is SimpleQuestionSeries -> SeriesType.SIMPLE_QUESTION
            else -> null
        }
    }

}


@Composable
fun PlanDefinitionIcon(
    color: ExuraRgba,
    iconSrc: String,
    size: CSSLengthOrPercentageValue = 24.px,
) {
    Box(
        modifier = Modifier
            .padding(8.px)
            .borderRadius(12.px)
            .backgroundColor(color.copy(alpha = 0.15)),
        contentAlignment = Alignment.Center,

        ) {
        Icon(
            iconSrc = iconSrc,
            size = size,
            color = color,
        )
    }
}

@Composable
fun InstructionIcon(
    size: CSSLengthOrPercentageValue = 24.px,
) {
    PlanDefinitionIcon(
        iconSrc = Assets.IconsUntiled.INSTRUCTION,
        color = ExuraColors.INSTRUCTION,
        size = size,
    )
}

@Composable
fun QuestionIcon(
    size: CSSLengthOrPercentageValue = 24.px,
) {
    PlanDefinitionIcon(
        iconSrc = Assets.IconsUntiled.QUESTION,
        color = ExuraColors.QUESTION,
        size = size,
    )
}

@Composable
fun PersistentIcon(
    size: CSSLengthOrPercentageValue = 24.px,
) {
    PlanDefinitionIcon(
        iconSrc = Assets.IconsUntiled.PERSISTENT,
        color = ExuraColors.PERSISTENT,
        size = size,
    )
}

@Composable
fun FloatingIcon(
    size: CSSLengthOrPercentageValue = 24.px,
) {
    PlanDefinitionIcon(
        iconSrc = Assets.IconsUntiled.FLOATING,
        color = ExuraColors.DYNAMIC,
        size = size,
    )
}
